import type { PdfMarkerRectangle } from './api.types';

type Box = { x: number; y: number; width: number; height: number };
type Glyph = Box & { text: string; vertical: boolean; breakAfter: boolean };

export type PdfTextGeometry = {
  width: number;
  height: number;
  glyphs: Glyph[];
};

// Measure the rendered glyphs, including PDF.js font scaling/rotation. These
// ranges are never installed as the browser selection.
export function measurePdfText(textDivs: HTMLElement[], surface: HTMLElement): PdfTextGeometry {
  const bounds = surface.getBoundingClientRect();
  const glyphs: Glyph[] = [];
  const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
  const range = document.createRange();
  for (const div of textDivs) {
    const matrix = new DOMMatrixReadOnly(getComputedStyle(div).transform);
    const vertical = Math.abs(matrix.b) > Math.abs(matrix.a);
    const walker = document.createTreeWalker(div, NodeFilter.SHOW_TEXT);
    let node: Node | null;
    while ((node = walker.nextNode())) {
      const text = node.textContent ?? '';
      for (const { segment, index } of segmenter.segment(text)) {
        range.setStart(node, index);
        range.setEnd(node, index + segment.length);
        const rect = range.getBoundingClientRect();
        const x = Math.max(0, rect.left - bounds.left);
        const y = Math.max(0, rect.top - bounds.top);
        const right = Math.min(bounds.width, rect.right - bounds.left);
        const bottom = Math.min(bounds.height, rect.bottom - bounds.top);
        if (right > x && bottom > y) {
          glyphs.push({
            x, y, width: right - x, height: bottom - y,
            text: segment.replaceAll('\0', ''), vertical, breakAfter: false,
          });
        }
      }
    }
    if (div.nextElementSibling?.tagName === 'BR' && glyphs.length) {
      glyphs[glyphs.length - 1].breakAfter = true;
    }
  }
  return { width: bounds.width, height: bounds.height, glyphs };
}

export function findPdfGlyph(geometry: PdfTextGeometry, x: number, y: number): number | null {
  let best: number | null = null;
  let distance = Infinity;
  geometry.glyphs.forEach((glyph, index) => {
    const dx = Math.max(glyph.x - x, 0, x - glyph.x - glyph.width);
    const dy = Math.max(glyph.y - y, 0, y - glyph.y - glyph.height);
    const current = Math.hypot(dx, dy);
    // Do not snap a stroke in blank space to unrelated text elsewhere.
    const tolerance = (glyph.vertical ? glyph.width : glyph.height) * 0.6;
    if (current <= tolerance && current < distance) {
      distance = current;
      best = index;
    }
  });
  return best;
}

function inlineGap(a: Box, b: Box, vertical: boolean): number {
  return vertical
    ? Math.max(0, a.y - b.y - b.height, b.y - a.y - a.height)
    : Math.max(0, a.x - b.x - b.width, b.x - a.x - a.width);
}

function sameLine(a: Box, b: Box, vertical: boolean): boolean {
  const overlap = vertical
    ? Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x)
    : Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y);
  return overlap >= 0.7 * (vertical ? Math.min(a.width, b.width) : Math.min(a.height, b.height));
}

export function selectPdfGlyphs(
  geometry: PdfTextGeometry, anchor: number, head: number,
): { text: string; rectangles: PdfMarkerRectangle[] } {
  const glyphs = geometry.glyphs.slice(Math.min(anchor, head), Math.max(anchor, head) + 1);
  const boxes: Box[] = [];
  let text = '';
  let previous: Glyph | undefined;
  for (const glyph of glyphs) {
    const line = previous && previous.vertical === glyph.vertical &&
      sameLine(previous, glyph, glyph.vertical) && !previous.breakAfter;
    const gap = previous ? inlineGap(previous, glyph, glyph.vertical) : 0;
    const thickness = glyph.vertical ? glyph.width : glyph.height;
    if (previous && !/\s$/.test(text) && !/^\s/.test(glyph.text)) {
      if (!line) text += '\n';
      else if (gap > thickness * 0.2) text += ' ';
    }
    text += glyph.text;
    const box = boxes[boxes.length - 1];
    if (box && line && gap <= thickness * 0.75 && sameLine(box, glyph, glyph.vertical)) {
      const right = Math.max(box.x + box.width, glyph.x + glyph.width);
      const bottom = Math.max(box.y + box.height, glyph.y + glyph.height);
      box.x = Math.min(box.x, glyph.x);
      box.y = Math.min(box.y, glyph.y);
      box.width = right - box.x;
      box.height = bottom - box.y;
    } else {
      boxes.push({ x: glyph.x, y: glyph.y, width: glyph.width, height: glyph.height });
    }
    previous = glyph;
  }
  return {
    text: text.trim(),
    rectangles: boxes.map(box => ({
      x: box.x / geometry.width, y: box.y / geometry.height,
      width: box.width / geometry.width, height: box.height / geometry.height,
    })),
  };
}
